import { PracticeArea, LegalVictory, Resource, FAQ, OfficeLocation, Document, LegalMatter } from '../types';

export const officeLocations: OfficeLocation[] = [
  {
    id: '1',
    name: 'Main Office – Jersey City',
    address: '551 Summit Ave',
    city: 'Jersey City',
    state: 'NJ',
    zip: '07306',
    phone: '+1 (201) 798-4100',
    lat: 40.7282,
    lng: -74.0505,
    hours: 'Mon–Fri: 8:00 AM – 7:00 PM'
  }
];

export const practiceAreas: PracticeArea[] = [
  {
    id: '1',
    slug: 'corporate-law',
    title: 'Corporate Law',
    icon: 'Building2',
    description: 'Strategic counsel for mergers, acquisitions, corporate governance, and complex business transactions.',
    longDescription: 'Our corporate law team provides comprehensive legal services to businesses of all sizes, from emerging startups to Fortune 500 companies. We guide clients through every stage of the business lifecycle, including formation, financing, growth, and succession planning. Our attorneys bring deep industry knowledge and a practical approach to solving complex legal challenges.',
    caseStudies: [
      {
        id: 'cs1',
        title: 'Multi-Million Dollar Merger Advisory',
        client: 'Confidential Fortune 500 Client',
        summary: 'Advised a leading technology company through a complex cross-border merger valued at $2.4 billion, navigating regulatory requirements across three jurisdictions.',
        outcome: 'Merger completed successfully ahead of schedule, creating the third-largest entity in the sector. Client achieved projected synergies within 18 months.',
        year: 2025,
        category: 'Mergers & Acquisitions',
        result: 'Successful Completion'
      },
      {
        id: 'cs2',
        title: 'Startup Series C Funding Round',
        client: 'HealthTech Innovations Inc.',
        summary: 'Led legal structuring and negotiation for a $85 million Series C funding round involving international venture capital firms.',
        outcome: 'Funding round closed with favorable terms for the client, including protective provisions and governance rights.',
        year: 2024,
        category: 'Venture Capital',
        result: '$85M Secured'
      }
    ]
  },
  {
    id: '2',
    slug: 'personal-injury',
    title: 'Personal Injury',
    icon: 'Shield',
    description: 'Aggressive advocacy for victims of negligence, securing maximum compensation for serious injuries.',
    longDescription: 'When you suffer an injury due to someone else\'s negligence, you need attorneys who will fight tirelessly for your rights. Our personal injury team has recovered over $500 million for our clients, including victims of medical malpractice, product liability, and catastrophic accidents. We work on a contingency fee basis — you pay nothing unless we win.',
    caseStudies: [
      {
        id: 'cs3',
        title: 'Major Construction Accident Settlement',
        client: 'Construction Worker',
        summary: 'Represented a worker severely injured in a scaffolding collapse due to employer negligence and OSHA violations. Pursued claims against multiple parties.',
        outcome: 'Secured $12.5 million settlement for the client, covering medical expenses, lost wages, pain and suffering, and future care needs.',
        year: 2025,
        category: 'Workplace Injury',
        result: '$12.5M Settlement'
      },
      {
        id: 'cs4',
        title: 'Medical Malpractice – Surgical Error',
        client: 'Confidential Client',
        summary: 'Pursued a medical malpractice claim after a surgical error led to permanent disability. Engaged top medical experts to establish the standard of care breach.',
        outcome: 'Jury verdict of $8.3 million including compensatory and punitive damages.',
        year: 2024,
        category: 'Medical Malpractice',
        result: '$8.3M Verdict'
      }
    ]
  },
  {
    id: '3',
    slug: 'family-law',
    title: 'Family Law',
    icon: 'Users',
    description: 'Compassionate representation in divorce, custody disputes, and complex family law matters.',
    longDescription: 'Family law matters require both legal expertise and emotional sensitivity. Our attorneys understand the challenges you face and work to protect your interests and your family\'s future. We handle high-net-worth divorces, contested custody cases, prenuptial agreements, and complex property division with discretion and professionalism.',
    caseStudies: [
      {
        id: 'cs5',
        title: 'High-Net-Worth Divorce Settlement',
        client: 'Confidential Client',
        summary: 'Represented a client in a contested divorce involving $45 million in assets across multiple jurisdictions, including offshore accounts and business interests.',
        outcome: 'Negotiated a favorable settlement that protected the client\'s primary business interests while achieving equitable distribution of marital assets.',
        year: 2025,
        category: 'Divorce',
        result: 'Favorable Settlement'
      },
      {
        id: 'cs6',
        title: 'Interstate Custody Dispute',
        client: 'Confidential Client',
        summary: 'Navigated complex interstate custody issues under the UCCJEA, securing primary custody for our client across state lines.',
        outcome: 'Court ruled in favor of our client, granting primary physical custody and establishing a workable visitation schedule.',
        year: 2024,
        category: 'Custody',
        result: 'Primary Custody Granted'
      }
    ]
  },
  {
    id: '4',
    slug: 'real-estate',
    title: 'Real Estate Law',
    icon: 'Home',
    description: 'Full-service real estate legal support from acquisition through development and leasing.',
    longDescription: 'Our real estate practice covers every aspect of commercial and residential property transactions. From due diligence and title review to negotiation and closing, we ensure your interests are protected at every stage. Our team has deep experience in zoning and land use, construction law, environmental compliance, and real estate litigation.',
    caseStudies: [
      {
        id: 'cs7',
        title: 'Mixed-Use Development Project',
        client: 'Metro Development Group',
        summary: 'Provided comprehensive legal services for a $200 million mixed-use development project, including zoning approvals, financing, construction contracts, and pre-sales.',
        outcome: 'Project completed on time and within budget, with all regulatory approvals secured. Pre-sale targets exceeded by 15%.',
        year: 2025,
        category: 'Commercial Development',
        result: '$200M Project Delivered'
      },
      {
        id: 'cs8',
        title: 'Commercial Lease Portfolio',
        client: 'National Retail Chain',
        summary: 'Negotiated and documented a portfolio of 45 commercial leases across the tri-state area, including anchor tenant agreements and build-out provisions.',
        outcome: 'Achieved an average savings of 18% on lease rates while securing favorable tenant improvement allowances.',
        year: 2024,
        category: 'Commercial Leasing',
        result: '18% Cost Savings'
      }
    ]
  },
  {
    id: '5',
    slug: 'estate-planning',
    title: 'Estate Planning',
    icon: 'FileText',
    description: 'Comprehensive wealth protection strategies including trusts, wills, and probate administration.',
    longDescription: 'Preserving your legacy requires careful planning and expert legal guidance. Our estate planning attorneys create customized strategies to protect your wealth, minimize tax obligations, and ensure your wishes are carried out. We handle everything from simple wills to complex multi-generational wealth transfer plans for high-net-worth families.',
    caseStudies: [
      {
        id: 'cs9',
        title: 'Multi-Generational Wealth Transfer',
        client: 'Confidential Family Office',
        summary: 'Designed and implemented a comprehensive estate plan for a family with $180 million in assets, utilizing irrevocable trusts, charitable giving vehicles, and tax optimization strategies.',
        outcome: 'Estimated estate tax savings of $45 million over three generations while maintaining family control of key business assets.',
        year: 2025,
        category: 'Wealth Transfer',
        result: '$45M Tax Savings'
      },
      {
        id: 'cs10',
        title: 'Complex Probate Litigation',
        client: 'Multiple Beneficiaries',
        summary: 'Represented beneficiaries in a contested probate matter involving contested wills, fiduciary breaches, and hidden assets totaling $12 million.',
        outcome: 'Court ruled in favor of our clients, removing the contested executor and recovering all misappropriated assets.',
        year: 2024,
        category: 'Probate Litigation',
        result: 'Full Asset Recovery'
      }
    ]
  },
  {
    id: '6',
    slug: 'immigration',
    title: 'Immigration Law',
    icon: 'Globe',
    description: 'Expert guidance through visas, green cards, citizenship, and employer immigration compliance.',
    longDescription: 'Navigating the U.S. immigration system requires experienced legal counsel. Our immigration team handles the full spectrum of immigration matters, from employment-based visas and family petitions to deportation defense and naturalization. We work with individuals, families, and businesses to achieve their immigration goals efficiently and effectively.',
    caseStudies: [
      {
        id: 'cs11',
        title: 'EB-1 Extraordinary Ability Petition',
        client: 'Distinguished Researcher',
        summary: 'Prepared and filed an EB-1 petition for a world-renowned researcher, documenting extraordinary ability through publications, citations, and industry recognition.',
        outcome: 'Petition approved without Request for Evidence (RFE), one of the fastest approvals in the category.',
        year: 2025,
        category: 'Employment Immigration',
        result: 'Approved Without RFE'
      },
      {
        id: 'cs12',
        title: 'L-1 Intracompany Transfer',
        client: 'International Technology Firm',
        summary: 'Managed L-1 visa applications for 15 executives and specialized knowledge workers transferring to U.S. operations, coordinating with foreign counsel.',
        outcome: 'All 15 petitions approved. U.S. office successfully established with full legal compliance.',
        year: 2024,
        category: 'Corporate Immigration',
        result: '15/15 Approved'
      }
    ]
  }
];

export const legalVictories: LegalVictory[] = [
  { id: '1', date: '2026-07-08', title: 'Record-breaking class action verdict of $47M against pharmaceutical negligence', category: 'Personal Injury', result: '$47M Verdict' },
  { id: '2', date: '2026-07-05', title: 'Successfully secured injunction blocking hostile takeover of mid-cap tech firm', category: 'Corporate Law', result: 'Injunction Granted' },
  { id: '3', date: '2026-07-02', title: 'Won landmark appellate ruling on interstate custody jurisdiction', category: 'Family Law', result: 'Ruling Reversed' },
  { id: '4', date: '2026-06-29', title: 'Closed $350M commercial real estate deal for international developer', category: 'Real Estate', result: '$350M Closed' },
  { id: '5', date: '2026-06-25', title: 'Approved O-1 visa for internationally acclaimed technology executive', category: 'Immigration', result: 'Visa Approved' },
  { id: '6', date: '2026-06-22', title: 'Settled multi-state employment discrimination class action for $18M', category: 'Employment Law', result: '$18M Settlement' },
  { id: '7', date: '2026-06-18', title: 'Secured emergency custody order protecting minor in international abduction case', category: 'Family Law', result: 'Child Protected' },
  { id: '8', date: '2026-06-15', title: 'Successfully challenged IRS assessment saving client $3.2M in corporate tax', category: 'Tax Law', result: '$3.2M Saved' },
];

export const resources: Resource[] = [
  { id: '1', title: 'Complete Guide to Personal Injury Claims', description: 'Everything you need to know about filing and winning a personal injury claim in New Jersey, from initial consultation through trial.', type: 'guide', category: 'Personal Injury', downloads: 2847, fileSize: '2.4 MB' },
  { id: '2', title: 'Business Formation Checklist', description: 'Step-by-step checklist for entrepreneurs forming a new business entity in New Jersey, including entity selection, registration, and compliance.', type: 'checklist', category: 'Corporate Law', downloads: 1932, fileSize: '1.1 MB' },
  { id: '3', title: 'Estate Planning Essentials', description: 'Comprehensive whitepaper on the fundamentals of estate planning, including wills, trusts, powers of attorney, and healthcare directives.', type: 'whitepaper', category: 'Estate Planning', downloads: 3156, fileSize: '3.8 MB' },
  { id: '4', title: 'Immigration Visa Guide 2026', description: 'Detailed guide covering all major employment and family-based visa categories, processing times, and requirements for 2026.', type: 'guide', category: 'Immigration', downloads: 4210, fileSize: '5.2 MB' },
  { id: '5', title: 'Real Estate Due Diligence Checklist', description: 'Essential due diligence checklist for commercial real estate transactions in New Jersey.', type: 'checklist', category: 'Real Estate', downloads: 1567, fileSize: '890 KB' },
  { id: '6', title: 'Protecting Your Digital Assets', description: 'Whitepaper on incorporating digital assets and cryptocurrency into your estate plan and business succession strategy.', type: 'whitepaper', category: 'Estate Planning', downloads: 2103, fileSize: '2.9 MB' },
];

export const faqs: FAQ[] = [
  { id: '1', question: 'How do I schedule a consultation?', answer: 'You can schedule a consultation by calling us at +1 (201) 798-4100, using our online booking form, or visiting our office at 551 Summit Ave, Jersey City. Initial consultations are available at a reduced rate, and some practice areas offer free initial consultations.', category: 'General' },
  { id: '2', question: 'What are your fees and payment options?', answer: 'Our fee structure varies by practice area. We offer flat fees for routine matters, hourly billing for complex litigation, and contingency fee arrangements for personal injury cases. We accept payment via check, credit card, and bank transfer. Payment plans may be available upon request.', category: 'General' },
  { id: '3', question: 'How long does a typical case take?', answer: 'Case timelines vary significantly depending on the type and complexity of the matter. Simple transactions may be completed in weeks, while complex litigation can take 1-3 years. During your consultation, we\'ll provide a realistic timeline based on your specific situation.', category: 'General' },
  { id: '4', question: 'What should I bring to my first consultation?', answer: 'Bring any documents related to your legal matter, including contracts, correspondence, court papers, medical records, or business documents. Also bring a photo ID and be prepared to provide a detailed timeline of events. The more information you bring, the better we can assess your case.', category: 'General' },
  { id: '5', question: 'How do I upload documents to the client portal?', answer: 'After your consultation, you\'ll receive login credentials for our secure client portal. From there, you can upload documents directly, track the status of your case, view invoices, and communicate securely with your attorney. We accept PDF, DOC, DOCX, and image file formats.', category: 'Client Portal' },
  { id: '6', question: 'Is my information kept confidential?', answer: 'Absolutely. All communications with our firm are protected by attorney-client privilege. Our client portal uses bank-level encryption (AES-256), and we maintain strict internal protocols to ensure the confidentiality of your information. We never share client information without explicit written consent.', category: 'Client Portal' },
  { id: '7', question: 'Can I track the progress of my case online?', answer: 'Yes, our client portal provides real-time updates on your case status. You can view milestones, upcoming deadlines, document status, and billing information. Your attorney will also provide regular updates via the portal and can be contacted directly through our secure messaging system.', category: 'Client Portal' },
  { id: '8', question: 'What practice areas do you cover?', answer: 'We handle a wide range of legal matters including Corporate Law, Personal Injury, Family Law, Real Estate, Estate Planning, and Immigration Law. Each practice area is led by experienced attorneys with deep specialization. Visit our Practice Areas page for detailed information.', category: 'Services' },
];

export const sampleDocuments: Document[] = [
  { id: '1', name: 'Retainer Agreement.pdf', type: 'PDF', uploadDate: '2026-06-15', status: 'approved', size: '245 KB' },
  { id: '2', name: 'Financial Disclosure Form.xlsx', type: 'XLSX', uploadDate: '2026-06-20', status: 'reviewed', size: '1.2 MB' },
  { id: '3', name: 'Witness Statement.pdf', type: 'PDF', uploadDate: '2026-06-28', status: 'pending', size: '890 KB' },
  { id: '4', name: 'Property Assessment.pdf', type: 'PDF', uploadDate: '2026-07-02', status: 'flagged', size: '3.4 MB' },
];

export const sampleMatters: LegalMatter[] = [
  {
    id: '1',
    title: 'Johnson v. Metro Development Corp.',
    caseNumber: 'CV-2026-04892',
    status: 'active',
    progress: 65,
    lastUpdate: '2026-07-08',
    attorney: 'Sarah Mitchell, Esq.',
    nextDeadline: '2026-07-25',
    description: 'Personal injury claim arising from construction site negligence. Currently in discovery phase.'
  },
  {
    id: '2',
    title: 'Thompson Estate Probate',
    caseNumber: 'PR-2026-01234',
    status: 'review',
    progress: 40,
    lastUpdate: '2026-07-05',
    attorney: 'David Chen, Esq.',
    nextDeadline: '2026-07-18',
    description: 'Probate administration for multi-asset estate. Pending court approval of inventory.'
  },
  {
    id: '3',
    title: 'Westfield Tech Corp. – Series C Financing',
    caseNumber: 'CL-2026-07891',
    status: 'active',
    progress: 80,
    lastUpdate: '2026-07-09',
    attorney: 'Michael Torres, Esq.',
    nextDeadline: '2026-07-12',
    description: 'Legal counsel for $85M Series C funding round. Final document review stage.'
  },
  {
    id: '4',
    name: 'Garcia Immigration Petition',
    caseNumber: 'IM-2026-03456',
    status: 'pending',
    progress: 25,
    lastUpdate: '2026-07-01',
    attorney: 'Lisa Park, Esq.',
    nextDeadline: '2026-08-15',
    description: 'EB-1 Extraordinary Ability petition preparation. Supporting documentation being compiled.'
  }
];
